import { Link } from "react-router";
import { Card, Stack } from "@salesapp/ui";
import { AllowanceCalculator } from "./AllowanceCalculator";

/**
 * Staff guide: how AI add-ons are priced, metered and limited. Admin console only,
 * never the customer help guide: it talks about Saleslox's model cost and margins.
 */
export function AiPricingGuidePage() {
  return (
    <Stack gap={5}>
      <div>
        <h1>How AI pricing works</h1>
        <p className="admin-muted">
          For staff pricing LeadGen, Loxley and Mailbox AI. Customers never see this page, Saleslox&apos;s model
          cost, tokens or cost limits.
        </p>
      </div>

      <Card>
        <Stack gap={3}>
          <h2>Two separate ledgers</h2>
          <p>
            The price a customer pays is not a wallet that AI calls draw money from. Every add-on has two ledgers that
            measure different things.
          </p>
          <table>
            <thead>
              <tr>
                <th>Add-on</th>
                <th>What the customer buys</th>
                <th>What it costs Saleslox</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>LeadGen</td>
                <td>A number of leads a month, e.g. 55 leads for A$49</td>
                <td>The model calls behind each search, capped by LeadGen&apos;s cost limit</td>
              </tr>
              <tr>
                <td>Loxley</td>
                <td>Seats, e.g. 3 seats at A$15 each (A$45 a month for all three)</td>
                <td>The model calls behind each request, capped by Loxley&apos;s cost limit for all seats together</td>
              </tr>
              <tr>
                <td>Mailbox AI</td>
                <td>A flat monthly fee</td>
                <td>The model calls that sort and summarise mail, capped by Mailbox AI&apos;s cost limit</td>
              </tr>
            </tbody>
          </table>
          <p>
            A LeadGen search uses up leads from the customer&apos;s 55 and cents from Saleslox&apos;s cost limit at the
            same time. &quot;What is left of the A$49&quot; really means &quot;what is left of the 55 leads&quot;.
          </p>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h2>What the customer sees: money used and left</h2>
          <p>
            Settings, AI services this month, shows each paid add-on as money used and money left, in the currency of
            the offer they paid. It is worked out from whichever limit runs out first:
          </p>
          <pre className="admin-json-view">
            {"share used = the larger of  (leads used / leads allowed)  and  (AI cost used / that feature's cost limit)\n"
              + "used       = monthly price x share used\n"
              + "left       = monthly price - used"}
          </pre>
          <p>
            Example: 30 of 55 leads used, AI cost well inside its limit. Share used is 30 / 55 = 54.5%, so the customer
            sees <strong>A$26.73 of A$49.00 used, A$22.27 left</strong>, and 30 of 55 leads. The figures reset on the
            1st of each month (UTC).
          </p>
          <ul>
            <li>
              Loxley and Mailbox AI have no unit to count, so their share is the AI cost used against their cost limit.
              Loxley&apos;s price is per seat x seats, and its one limit covers all seats together.
            </li>
            <li>
              The organization&apos;s total AI budget stops every feature when it runs out, so a used-up total shows
              every add-on as fully used.
            </li>
            <li>Before an offer has been paid (a trial), the customer sees a percentage only, with no money.</li>
            <li>
              The customer never sees tokens, model cost, the cost limits or the total budget: none of it is sent to
              their browser. Tokens could be multiplied by public model prices to work out Saleslox&apos;s margin.
            </li>
            <li>
              A customer on their own AI connection pays their provider, so only the units count, and they do see
              their own tokens.
            </li>
          </ul>
          <p>
            These rules are the same ones that refuse an AI call, so &quot;left&quot; reaches zero exactly when the
            add-on stops working.
          </p>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h2>Cost limits (Organization AI card)</h2>
          <ul>
            <li>
              <strong>Monthly budget</strong> is the most the organization&apos;s AI calls may cost Saleslox in a month,
              in US dollars. It covers every user and every seat together.
            </li>
            <li>
              <strong>A feature limit</strong> (LeadGen, Loxley, Mailbox AI) is that feature&apos;s share of the budget.
              It stops at its limit, and the limit is kept for it: no other feature can use it.
            </li>
            <li>
              <strong>Features left blank</strong> share what is left of the budget after the limits.
            </li>
            <li>The feature limits together can never be more than the budget. The card, the API and the database all refuse it.</li>
          </ul>
          <p>
            <strong>The cost limit must cover the whole allowance.</strong> If LeadGen&apos;s limit runs out at lead 40,
            the customer paid for 55 and cannot use 15, and their screen shows the add-on as fully used. Size the limit
            with the calculator below.
          </p>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h2>Sizing a customer</h2>
          <ol>
            <li>
              <strong>Units from the price.</strong> The rate card sets a price per lead: A$49 for 55 leads is A$0.89 a
              lead, so a customer who wants to pay A$70 gets 70 / 0.89 = 78 leads, rounded down.
            </li>
            <li>
              <strong>Cost at full use.</strong> Leads x the measured AI cost per lead. Loxley: seats x requests per
              seat x cost per request. Size Loxley for a heavy user, not the average. Mailbox AI is a flat fee with no
              unit sold, so size it by emails sorted a month x cost per email (which includes the occasional mailbox
              summary), for a busy month.
            </li>
            <li>
              <strong>Cost limit.</strong> Full-use cost plus headroom (25% by default), so a heavy month does not stop
              the customer early. Enter it on the Organization AI card, and raise the monthly budget to cover all the
              limits.
            </li>
            <li>
              <strong>Margin.</strong> Price in US dollars (via the Billing FX table) minus full-use cost. &quot;Most the
              price carries at target&quot; is the largest allowance that still keeps the target margin.
            </li>
            <li>
              Put the prices in the customer&apos;s AI billing card, the leads in the Lead generator card and the seats in
              the Loxley card. Mailbox AI has only its switch: its cost limit is what bounds it.
            </li>
          </ol>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h2>Where AI cost comes from</h2>
          <p>
            Every model call records its tokens. Cost = tokens x the per-million rates on the{" "}
            <Link to="/ai">AI page</Link>, or, when those rates are 0, the model&apos;s published price (cache hit and
            miss, peak and off-peak). Only calls on Saleslox&apos;s connection cost Saleslox anything.
          </p>
          <p>
            <strong>If the model has no published price and the rates are 0, every call is recorded at $0.00.</strong>{" "}
            Then no cost limit ever stops anything and every measured cost reads $0. The calculator warns when this is
            the case. Fix it by entering the input and output rates on the AI page.
          </p>
        </Stack>
      </Card>

      <AllowanceCalculator />
    </Stack>
  );
}
